import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Circle } from "lucide-react";
import { api, errMsg, fileUrl, youtubeId } from "@/lib/api";
import PdfPreview from "@/components/PdfPreview";
import { useApp } from "@/context/AppContext";
import { Progress } from "@/components/ui/progress";

export default function Learn() {
  const { productId } = useParams();
  const { pick } = useApp();
  const [data, setData] = useState(null);
  const [cur, setCur] = useState(0);
  useEffect(() => { api.get(`/learn/${productId}`).then((r) => setData(r.data)).catch((e) => setData({ error: errMsg(e) })); }, [productId]);
  if (!data) return <div className="min-h-screen bg-[#1A1412]" />;
  if (data.error) return <div className="min-h-screen grid place-items-center" data-testid="learn-error"><div className="text-center space-y-4"><p>{data.error}</p><Link to="/account" className="underline">Back</Link></div></div>;
  const { course, enrollment } = data;
  const lesson = course.lessons[cur];
  const done = enrollment.completed_lessons || [];
  const pct = Math.round((100 * done.length) / Math.max(1, course.lessons.length));
  const yt = youtubeId(lesson?.video_url);

  const complete = async () => {
    try {
      const r = await api.post(`/learn/${productId}/lessons/${lesson.lesson_id}/complete`);
      setData({ ...data, enrollment: r.data });
      if (r.data.certificate_id && !enrollment.certificate_id) toast.success("Course completed — certificate issued!");
      if (cur < course.lessons.length - 1) setCur(cur + 1);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="min-h-screen bg-[#1A1412] text-white flex flex-col lg:flex-row" data-testid="learn-page">
      <div className="flex-1 flex flex-col">
        <div className="h-16 px-6 flex items-center gap-4 border-b border-white/10">
          <Link to="/account" data-testid="learn-back-btn" aria-label="Back"><ArrowLeft size={18} /></Link>
          <p className="font-serif text-xl truncate">{pick(course, "name")}</p>
        </div>
        <div className="aspect-video bg-black w-full">
          {lesson && (yt ? <iframe key={lesson.lesson_id} title={lesson.title} className="w-full h-full" src={`https://www.youtube.com/embed/${yt}`} allowFullScreen />
            : <video key={lesson.lesson_id} src={fileUrl(lesson.video_url)} controls controlsList="nodownload" className="w-full h-full" data-testid="lesson-video" onContextMenu={(e) => e.preventDefault()} />)}
        </div>
        <div className="p-6 sm:p-10 flex flex-wrap items-center justify-between gap-4">
          <div><p className="font-mono text-xs text-[#C47A37] tracking-[0.2em]">LESSON {cur + 1}/{course.lessons.length}</p><h1 className="font-serif text-3xl mt-1" data-testid="lesson-title">{lesson?.title}</h1></div>
          {lesson && (done.includes(lesson.lesson_id)
            ? <span className="flex items-center gap-2 text-[#8FD19E] text-sm"><CheckCircle2 size={16} />Completed</span>
            : <button onClick={complete} data-testid="mark-complete-btn" className="bg-white text-[#3B220C] px-6 py-3 text-sm">Mark as complete</button>)}
        </div>
      </div>
      <aside className="lg:w-96 border-l border-white/10 bg-[#3B220C]">
        <div className="p-6 space-y-3 border-b border-white/10">
          <div className="flex justify-between text-sm"><span>Progress</span><span data-testid="course-progress">{pct}%</span></div>
          <Progress value={pct} className="h-1 bg-white/10" />
          {enrollment.certificate_id && (
            <PdfPreview path={`/certificates/${enrollment.certificate_id}/pdf`} filename={`${enrollment.certificate_id}.pdf`} label="Preview & print certificate" testid="learn-certificate-btn"
              className="w-full mt-3 justify-center !bg-[#C47A37] !border-[#C47A37] py-3 text-white" />
          )}
        </div>
        {course.lessons.map((l, i) => (
          <button key={l.lesson_id} onClick={() => setCur(i)} data-testid={`lesson-item-${i}`}
            className={`w-full text-left px-6 py-4 flex items-center gap-3 text-sm border-b border-white/5 transition-colors ${i === cur ? "bg-white/10" : "hover:bg-white/5"}`}>
            {done.includes(l.lesson_id) ? <CheckCircle2 size={16} className="text-[#8FD19E]" /> : <Circle size={16} className="text-white/40" />}
            <span className="flex-1">{i + 1}. {l.title}</span><span className="text-xs text-white/40">{l.duration}</span>
          </button>
        ))}
      </aside>
    </div>
  );
}
